'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { Children, cloneElement, PureComponent, createRef } from 'react';
import shallowEqual from 'react-utils/shallowEqual';
import styled from 'styled-components';
import { isIE11 } from '../utils/BrowserTest';
import { callIfPossible } from '../core/Functions';
import { warnIfFragment } from '../utils/devWarnings';
import { getComponentPropType } from '../utils/propTypes/componentProp';
import { cancelSchedulerCallback, requestSchedulerCallback } from '../utils/Timers';
import UIOverhang from './UIOverhang';
const sides = ['top', 'bottom', 'left', 'right'];
const Wrapper = styled.div.withConfig({
  displayName: "UIScrollOverhang__Wrapper",
  componentId: "m2voxj-0"
})(["position:relative;min-height:0;"]);

class UIScrollOverhang extends PureComponent {
  constructor(...args) {
    super(...args);

    this.handleScroll = evt => {
      callIfPossible(this.props.onScroll, evt);
      this.delayedMeasureScrollableArea();
    };

    this.delayedMeasureScrollableArea = () => {
      cancelSchedulerCallback(this._timeout); // Ensure only one callback is queued at a time

      this._timeout = requestSchedulerCallback(this.measureScrollableArea);
    };

    this.measureScrollableArea = () => {
      const thisEl = this.wrapperRef.current; // Bail out if our only children are the 4 scroll overhang elements

      if (thisEl == null || thisEl.children.length === 4) return;
      const childEl = thisEl.children[0];
      const computedStyles = getComputedStyle(childEl, null); // Patch for Firefox < 62: https://bugzilla.mozilla.org/show_bug.cgi?id=1467722

      if (computedStyles == null) return;
      const {
        clientHeight,
        clientWidth,
        scrollHeight,
        scrollLeft,
        scrollTop,
        scrollWidth
      } = childEl;

      const isScrollable = overflow => overflow === 'auto' || overflow === 'scroll';

      const scrollsVertically = isScrollable(computedStyles.overflowY);
      const scrollsHorizontally = isScrollable(computedStyles.overflowX);
      const measuredScrollableArea = {
        top: scrollsVertically ? scrollTop : 0,
        bottom: scrollsVertically ? scrollHeight - clientHeight - scrollTop : 0,
        left: scrollsHorizontally ? scrollLeft : 0,
        right: scrollsHorizontally ? scrollWidth - clientWidth - scrollLeft : 0
      };

      if (!shallowEqual(measuredScrollableArea, this.state.measuredScrollableArea)) {
        this.setState({
          measuredScrollableArea
        });
      }
    };

    this.state = {
      measuredScrollableArea: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      }
    };
    this.wrapperRef = /*#__PURE__*/createRef();
  }

  componentDidMount() {
    // IE needs a longer delay before measurement, or else it sometimes returns an absurdly large
    // scrollWidth.
    if (isIE11()) {
      this._frame = requestAnimationFrame(() => {
        this._frame = requestAnimationFrame(this.measureScrollableArea);
      });
    } else {
      this.delayedMeasureScrollableArea();
    }

    const thisEl = this.wrapperRef.current;
    this.resizeObserver = new ResizeObserver(this.delayedMeasureScrollableArea);
    this.resizeObserver.observe(thisEl);
  }

  componentDidUpdate(prevProps, prevState) {
    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.measuredScrollableArea === this.state.measuredScrollableArea) {
      this.delayedMeasureScrollableArea();
    }

    const thisEl = this.wrapperRef.current;
    this.resizeObserver = new ResizeObserver(this.delayedMeasureScrollableArea);
    this.resizeObserver.observe(thisEl);
  }

  componentWillUnmount() {
    const thisEl = this.wrapperRef.current;
    this.resizeObserver.unobserve(thisEl);
    cancelSchedulerCallback(this._timeout);
    cancelAnimationFrame(this._frame);
  }

  getClonedChild(children, measuredScrollableArea) {
    const child = Children.only(children);
    warnIfFragment(children, UIScrollOverhang.displayName);
    const childTabIndex = child.props && child.props.tabIndex;
    const childIsScrollable = sides.some(side => measuredScrollableArea[side] > 0);
    const clonedElement = /*#__PURE__*/cloneElement(child, {
      tabIndex: childIsScrollable ? childTabIndex || 0 : childTabIndex,
      onScroll: this.handleScroll
    });
    return clonedElement;
  }

  render() {
    const _this$props = this.props,
          {
      children,
      forceOverhangs,
      gradientColor,
      Overhang,
      size
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["children", "forceOverhangs", "gradientColor", "Overhang", "size", "onScroll"]);

    const {
      measuredScrollableArea
    } = this.state;
    const clonedChild = this.getClonedChild(children, measuredScrollableArea);
    const overhangProps = {
      gradientColor,
      size
    }; // Fade smoothly from 1 (scrollable area exceeds overhang size) to 0 (no scrollable area)

    const getOpacity = side => {
      if (size === 0) {
        return 0;
      }

      return forceOverhangs[side] ? 1 : Math.min((measuredScrollableArea[side] || 0) / size, 1);
    };

    return /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, rest, {
      ref: this.wrapperRef,
      children: [clonedChild, /*#__PURE__*/_jsx(Overhang, Object.assign({}, overhangProps, {
        opacity: getOpacity('top'),
        side: "top"
      })), /*#__PURE__*/_jsx(Overhang, Object.assign({}, overhangProps, {
        opacity: getOpacity('bottom'),
        side: "bottom"
      })), /*#__PURE__*/_jsx(Overhang, Object.assign({}, overhangProps, {
        opacity: getOpacity('left'),
        side: "left"
      })), /*#__PURE__*/_jsx(Overhang, Object.assign({}, overhangProps, {
        opacity: getOpacity('right'),
        side: "right"
      }))]
    }));
  }

}

UIScrollOverhang.propTypes = {
  children: PropTypes.element.isRequired,
  forceOverhangs: PropTypes.shape({
    top: PropTypes.bool,
    bottom: PropTypes.bool,
    left: PropTypes.bool,
    right: PropTypes.bool
  }).isRequired,
  gradientColor: UIOverhang.propTypes.gradientColor,
  onScroll: PropTypes.func,
  Overhang: getComponentPropType(UIOverhang),
  size: UIOverhang.propTypes.size
};
UIScrollOverhang.defaultProps = {
  forceOverhangs: {
    top: false,
    bottom: false,
    left: false,
    right: false
  },
  gradientColor: UIOverhang.defaultProps.gradientColor,
  Overhang: UIOverhang,
  size: UIOverhang.defaultProps.size
};
UIScrollOverhang.displayName = 'UIScrollOverhang';
export default UIScrollOverhang;